import React, { useEffect, useState, useCallback, createContext, useContext } from 'react'

// ── Types ──

type ToastType = 'success' | 'error' | 'info'

interface ToastItem {
  id: number
  message: string
  type: ToastType
  action?: { label: string; onClick: () => void }
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType, action?: { label: string; onClick: () => void }) => void
}

// ── Context ──

const ToastContext = createContext<ToastContextValue>({ toast: () => {} })

export const useToast = () => useContext(ToastContext)

// ── Single toast ──

const DURATION = 4000
const DURATION_WITH_ACTION = 8000

const TYPE_STYLES: Record<ToastType, string> = {
  success: 'bg-primary text-white',
  error: 'bg-red-600 text-white',
  info: 'bg-gray-800 text-white',
}

const TYPE_ICONS: Record<ToastType, string> = {
  success: 'M5 13l4 4L19 7',
  error: 'M6 18L18 6M6 6l12 12',
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
}

const ToastMessage: React.FC<{ item: ToastItem; onDismiss: (id: number) => void }> = ({ item, onDismiss }) => {
  useEffect(() => {
    const duration = item.action ? DURATION_WITH_ACTION : DURATION
    const timer = setTimeout(() => onDismiss(item.id), duration)
    return () => clearTimeout(timer)
  }, [item.id, item.action, onDismiss])

  return (
    <div
      className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${TYPE_STYLES[item.type]} animate-[slideUp_0.2s_ease-out]`}
      role="status"
      aria-live="polite"
    >
      <svg aria-hidden="true" className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d={TYPE_ICONS[item.type]} />
      </svg>
      <span className="flex-1">{item.message}</span>
      {item.action && (
        <button
          onClick={() => { item.action!.onClick(); onDismiss(item.id) }}
          className="ml-2 px-2 py-0.5 text-xs font-semibold bg-white/20 hover:bg-white/30 rounded transition-colors"
        >
          {item.action.label}
        </button>
      )}
    </div>
  )
}

// ── Provider ──

let nextId = 0

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback((message: string, type: ToastType = 'success', action?: { label: string; onClick: () => void }) => {
    setToasts((prev) => [...prev, { id: ++nextId, message, type, action }])
  }, [])

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {toasts.length > 0 && (
        <div className="fixed bottom-6 right-6 z-toast flex flex-col gap-2 max-w-sm">
          {toasts.map((t) => (
            <ToastMessage key={t.id} item={t} onDismiss={dismiss} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  )
}
